import { Box, Icon, Image, Theme, Typography } from "@outschool/backpack";
import { imageUrl } from "@outschool/filestack-urls";
import { Leader } from "@outschool/gql-backend-generated";
import {
  LightListingCard_ListingFragment,
  SaveButton_ActivityFragment,
  SearchListingsQuery,
} from "@outschool/gql-frontend-generated";
import { faCalendarAlt, fasStar } from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import { measurePerformance } from "@outschool/ui-analytics";
import {
  decimalFormatter,
  useActivityString,
} from "@outschool/ui-components-website";
import { useDevicePixelRatio, useDeviceTypeContext } from "@outschool/ui-utils";
import React from "react";

import { useIsActivitySavedByCurrentUser } from "../saving/graphql/hooks";
import { SaveHeart } from "../saving/SaveButton";

type CardListing = SearchListingsQuery["searchListings"]["listings"][number];

function Rating({ listing }: { listing: LightListingCard_ListingFragment }) {
  const { t } = useTranslation("client\\components\\listings\\MiniListing");
  const roundedRating =
    Math.round((listing.activity.averageStarRating as number) * 2) / 2.0;

  const hasRatings = listing.activity.reviewCount > 0;

  return hasRatings ? (
    <>
      <Icon
        sx={{
          color: "#FDC840",
          paddingRight: "0.25em",
        }}
        icon={fasStar}
      />
      {/* eslint-disable i18next/no-literal-string */}
      <>
        {decimalFormatter(roundedRating, {
          // @ts-ignore TS(2322): Type 'number' is not assignable to type 'undefined... Remove this comment to see the full error message
          minimumFractionDigits: 1,
        })}{" "}
        ({listing.activity.reviewCount})
      </>
      {/* eslint-enable i18next/no-literal-string */}
    </>
  ) : (
    <span>{t("New class")}</span>
  );
}

type ListingProps = {
  listing: {
    activity:
      | Pick<
          CardListing["activity"],
          | "uid"
          | "details"
          | "age_min"
          | "age_max"
          | "weekly_meetings"
          | "duration_weeks"
          | "is_ongoing_weekly"
          | "isClub"
          | "isFlexSchedule"
          | "isOneTime"
          | "hasTeacherSchedule"
        > &
          SaveButton_ActivityFragment;
    leader: Pick<
      Leader,
      "name" | "reviewCount" | "photo" | "averageActivityStarRating"
    >;
    insertionId?: string;
  };
  alignText?: string;
  imageSize?: number;
  listingIndex?: number;
  showSaved?: boolean;
  showFrequency?: boolean;
  showTeacher?: boolean;
  variant?: string;
};

export default function MiniListing({
  listing,
  variant,
  alignText = "center",
  imageSize = 72,
  listingIndex = -1,
  showSaved = false,
  showFrequency = true,
}: ListingProps) {
  const { translate } = useMachineTranslation();
  const { ageRangeString, durationString } = useActivityString();
  const { deviceType } = useDeviceTypeContext();
  const pixelRatio = useDevicePixelRatio(deviceType);
  const isSaved = useIsActivitySavedByCurrentUser(listing.activity);
  const photo = imageUrl(listing.activity.details.photo, {
    height: imageSize * pixelRatio,
    width: imageSize * pixelRatio,
    fit: "crop",
  });

  return (
    <Box
      flex
      sx={{
        color: "grey.900",
        alignItems: alignText,
      }}
    >
      <Box
        sx={{
          paddingRight: "12px",
          position: "relative",
          alignSelf: "center",
        }}
      >
        <Image
          sx={{
            width: imageSize,
            height: imageSize,
            borderRadius: "12px",
            objectFit: "cover",
            display: "block",
          }}
          src={photo}
          height={imageSize}
          width={imageSize}
          /* eslint-disable i18next/no-literal-string */
          alt={"Class listing photo"}
          // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
          onLoad={
            listingIndex === 0
              ? () => {
                  measurePerformance("os-page-load", {
                    source: "image",
                    variant,
                  });
                }
              : null
          }
        />
        {showSaved && (
          <SaveHeart
            isSet={isSaved}
            activity={listing.activity}
            // @ts-ignore TS(2322): Type 'Pick<Leader, "reviewCount" | "name" | "photo... Remove this comment to see the full error message
            leader={listing.leader}
            sx={{
              fontSize: "24px",
              position: "absolute",
              top: "6px",
              right: "18px",
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          textAlign: "start",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "grey.500",
          }}
        >
          {/* eslint-disable i18next/no-literal-string */}
          <Rating
            listing={listing as LightListingCard_ListingFragment}
          /> · {ageRangeString(listing.activity, true)}
          {/* eslint-enable i18next/no-literal-string */}
        </Typography>

        <Typography
          variant="h4"
          sx={(theme: Theme) => ({
            fontSize: "1.3rem",
            [theme.breakpoints.up("md")]: {
              // Override the h4 variant fontSize at md breakpoint
              fontSize: "1.3rem",
            },
            lineHeight: "2rem",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: "2",
            overflow: "hidden",
            paddingBottom: 0,
            fontFamily: "'Ginto Normal',sans-serif",
          })}
        >
          {translate(listing.activity.title)}
        </Typography>

        {showFrequency && (
          <Box
            sx={{
              fontSize: "1.2rem",
              color: "grey.500",
              paddingTop: "0.25em",
            }}
          >
            <Icon icon={faCalendarAlt} /> {durationString(listing.activity)}
          </Box>
        )}
      </Box>
    </Box>
  );
}
